import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { LeadInput, LeadResult } from "./contact-schema";

/**
 * Lead intake for the public contact + proposal forms. The table is
 * server-write only (no anon insert grant), so every submission passes
 * through this validated, rate-limited, spam-screened path.
 */
export const submitLead = createServerFn({ method: "POST" })
  .inputValidator((input: LeadInput) => input)
  .handler(async ({ data }): Promise<LeadResult> => {
    const { processLead } = await import("./contact-lead.server");
    return processLead(data);
  });

const replySchema = z.object({
  inquiryId: z.string().uuid(),
  toEmail: z.string().trim().email(),
  toName: z.string().trim(),
  subject: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(5000),
});

/** Staff reply function to send direct email responses to leads via Resend. */
export const replyToLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => replySchema.parse(input))
  .handler(async ({ data }) => {
    const { sendEmail, brandedHtml } = await import("./email.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const result = await sendEmail({
      to: data.toEmail,
      subject: data.subject,
      html: brandedHtml(data.message, `Hello ${data.toName}`),
      replyTo: "connect@finendra.com",
    });

    await supabaseAdmin
      .from("contact_submissions")
      .update({ status: "contacted", status_updated_at: new Date().toISOString() })
      .eq("id", data.inquiryId);

    return { ok: true as const, emailId: result.id };
  });

const inboundSchema = z.object({
  fromEmail: z.string().trim().email(),
  fromName: z.string().trim().optional(),
  recipientEmail: z.string().trim().min(1),
  subject: z.string().trim().min(1),
  message: z.string().trim().min(1),
});

/** Server function to record an incoming email sent to <anything>@finendra.com */
export const receiveInboundEmail = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inboundSchema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const senderName = data.fromName || data.fromEmail.split("@")[0] || "Unknown Sender";
    const fullMessage = `Subject: ${data.subject}\n\n${data.message}`;

    const { data: row, error } = await supabaseAdmin
      .from("contact_submissions")
      .insert({
        name: senderName,
        email: data.fromEmail,
        service: `To: ${data.recipientEmail}`,
        message: fullMessage,
        source: "email_inbound",
        status: "new",
        handled: false,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return { ok: true as const, id: row.id };
  });
